/**
 * Who can watch (section 4): four plain choices, then what unlocks it, picked from what the brand
 * really sells (GET /admin/v1/products, with prices) and what its ticket codes unlock. No product
 * id is ever typed. A rental brings the rental window, prefilled from the product.
 */

import { useEffect, useId, useState } from "react";
import Checkbox from "@web-ui/components/Checkbox";
import Field from "@web-ui/components/Field";
import { grants } from "@/api/admin";
import { href } from "@/lib/route";
import { useStudio } from "@/shell/studio";
import { lower, noun } from "@/vocabulary/vocabulary";
import { accessView, entitlementFor, offersFor, productLabel, type AccessChoice, type Entitlement, type OtherUnlock } from "../access";
import { useForm } from "../form-context";
import { fieldDomId } from "../paths";

export function AccessSection() {
  const form = useForm();
  const { client, pack, products } = useStudio();
  const [others, setOthers] = useState<OtherUnlock[]>([]);
  const groupId = useId();
  useEffect(() => {
    let live = true;
    grants.batches(client).then((r) => { if (live) setOthers(r.batches.filter((b) => !b.revoked).map((b) => ({ productId: b.productId, label: `Ticket codes “${b.label}”` }))); }).catch(() => undefined);
    return () => { live = false; };
  }, [client]);

  const sold = products ?? [];
  const ent = form.doc["entitlement"] as Entitlement | undefined;
  const view = accessView(ent, sold);
  const offers = offersFor(sold, others);
  const one = lower(noun(pack, form.type));
  const error = form.issue("entitlement")?.message;
  const write = (choice: AccessChoice, ticked: string[]) => { form.set("entitlement", entitlementFor(choice, ticked, sold, ent)); form.touch("entitlement"); };
  // Products the item names that are neither sold nor unlocked by codes: kept, shown as they are.
  const unknown = view.products.filter((id) => !sold.some((p) => p.productId === id) && !others.some((o) => o.productId === id));

  const choices: Array<{ id: AccessChoice; label: string; detail: string; when: boolean }> = [
    { id: "free", label: "Everyone, free", detail: `Anyone with the app can watch this ${one}.`, when: true },
    { id: "members", label: "Members", detail: "People with a membership.", when: offers.memberships.length > 0 || view.choice === "members" },
    { id: "members-or-buy", label: "Members, or anyone who rents or buys it", detail: "Members watch it as part of their membership; others can pay for this one.", when: (offers.memberships.length > 0 && offers.single.length > 0) || view.choice === "members-or-buy" },
    { id: "buy", label: "Only people who rent or buy it", detail: "Not part of the membership.", when: offers.single.length > 0 || view.choice === "buy" },
  ];

  return (
    <div className="flex flex-col gap-5">
      <fieldset id={fieldDomId("entitlement")} tabIndex={-1} className="flex flex-col gap-2 outline-none" aria-describedby={error ? `${groupId}-error` : undefined}>
        <legend className="sr-only">Who can watch</legend>
        {choices.filter((c) => c.when).map((c) => (
          <label key={c.id} className={`flex cursor-pointer items-start gap-3 rounded-2xl border-2 p-3 transition-colors ${view.choice === c.id ? "border-accent bg-b-surface1" : "border-s-subtle hover:border-s-stroke2"} ${form.canEdit ? "" : "pointer-events-none"}`}>
            <input type="radio" name={`${groupId}-access`} className="mt-1 size-4 accent-accent" checked={view.choice === c.id} disabled={!form.canEdit} onChange={() => write(c.id, view.products)} />
            <span className="flex flex-col">
              <span className="text-body-2 font-medium">{c.label}</span>
              <span className="text-caption text-t-secondary">{c.detail}</span>
            </span>
          </label>
        ))}
        {error ? <p id={`${groupId}-error`} className="textfield__error" role="alert">{error}</p> : null}
      </fieldset>

      {view.choice === "members" || view.choice === "members-or-buy" ? (
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sub-title-2">Which memberships</legend>
          {offers.memberships.map((p) => (
            <Checkbox key={p.productId} label={productLabel(p, client.brand.id)} checked={view.products.includes(p.productId)} disabled={!form.canEdit}
              onChange={(on) => write(view.choice, on ? [...view.products, p.productId] : view.products.filter((x) => x !== p.productId))} />
          ))}
        </fieldset>
      ) : null}

      {view.choice === "members-or-buy" || view.choice === "buy" ? (
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sub-title-2">What viewers can rent, buy or unlock</legend>
          {offers.single.map((p) => (
            <Checkbox key={p.productId} label={p.kind === "other" ? p.label : productLabel(p, client.brand.id)} checked={view.products.includes(p.productId)} disabled={!form.canEdit}
              onChange={(on) => write(view.choice, on ? [...view.products, p.productId] : view.products.filter((x) => x !== p.productId))} />
          ))}
        </fieldset>
      ) : null}

      {ent?.rental && view.choice !== "free" ? (
        <div className="grid max-w-lg gap-3 sm:grid-cols-2">
          <Field id={fieldDomId("entitlement.rental.startWithinDays")} label="Days to start watching after renting" type="number" min={1} value={String(ent.rental.startWithinDays)} disabled={!form.canEdit}
            onChange={(e) => form.set("entitlement.rental.startWithinDays", Math.max(1, Math.round(Number(e.target.value) || 1)))} />
          <Field id={fieldDomId("entitlement.rental.watchWithinHours")} label="Hours to finish once started" type="number" min={1} value={String(ent.rental.watchWithinHours)} disabled={!form.canEdit}
            onChange={(e) => form.set("entitlement.rental.watchWithinHours", Math.max(1, Math.round(Number(e.target.value) || 1)))} />
        </div>
      ) : null}

      {unknown.length ? <p className="text-caption text-t-secondary">{`Also unlocked by something sold elsewhere: ${unknown.join(", ")}.`}</p> : null}
      {products && sold.length === 0 ? <p className="text-body-2 text-t-secondary">Nothing is sold yet, so everything is free. <a className="underline" href={href({ brand: client.brand.id, section: "pricing" })}>Pricing & access</a> explains how to start selling.</p> : null}
    </div>
  );
}
